import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import UserCalendarGrid from './components/UserCalendarGrid';
import AppointmentRequestModal from './components/AppointmentRequestModal';
import UserInfoPanel from './components/UserInfoPanel';


const UserCalendarView = () => {
  const location = useLocation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');
  const [selectedUser, setSelectedUser] = useState('diane');
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  // Get user from URL params or default
  const userId = new URLSearchParams(location?.search)?.get('user') || 'diane';

  const users = {
    diane: {
      id: 'diane',
      name: 'Diane Silva',
      title: 'Mentora Espiritual',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b9b8e0ea?w=128&h=128&fit=crop&crop=face',
      specialties: ['Aconselhamento', 'Teologia', 'Mentoria'],
      bio: 'Especialista em aconselhamento bíblico com mais de 10 anos de experiência.',
      availability: 'Segunda a Sexta: 9h às 17h',
      rating: 4.9,
      totalSessions: 156
    },
    lindsey: {
      id: 'lindsey',
      name: 'Lindsey Santos',
      title: 'Conselheira Cristã',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=128&h=128&fit=crop&crop=face',
      specialties: ['Família', 'Relacionamentos', 'Jovens'],
      bio: 'Focada em ministério familiar e aconselhamento de relacionamentos.',
      availability: 'Terça a Sábado: 10h às 18h',
      rating: 4.8,
      totalSessions: 89
    }
  };

  // Mock events - showing availability and occupied slots
  const mockUserEvents = {
    diane: [
      {
        id: 1,
        title: 'Disponível',
        start: new Date(2025, 0, 28, 10, 0),
        end: new Date(2025, 0, 28, 11, 0),
        type: 'available',
        isPublic: true
      },
      {
        id: 2,
        title: 'Ocupado',
        start: new Date(2025, 0, 28, 14, 0),
        end: new Date(2025, 0, 28, 15, 0),
        type: 'busy',
        isPublic: false
      },
      {
        id: 3,
        title: 'Disponível',
        start: new Date(2025, 0, 29, 9, 0),
        end: new Date(2025, 0, 29, 10, 0),
        type: 'available',
        isPublic: true
      }
    ],
    lindsey: [
      {
        id: 4,
        title: 'Disponível',
        start: new Date(2025, 0, 28, 15, 0),
        end: new Date(2025, 0, 28, 16, 0),
        type: 'available',
        isPublic: true
      },
      {
        id: 5,
        title: 'Ocupado',
        start: new Date(2025, 0, 29, 11, 0),
        end: new Date(2025, 0, 29, 12, 0),
        type: 'busy',
        isPublic: false
      }
    ]
  };

  const currentUser = users?.[selectedUser] || users?.diane;
  const userEvents = mockUserEvents?.[selectedUser] || [];

  const userOptions = [
    { value: 'diane', label: 'Calendário da Diane' },
    { value: 'lindsey', label: 'Calendário da Lindsey' }
  ];

  const viewModeOptions = [
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mês' }
  ];

  // Navigation handlers
  const goToPrevious = () => {
    if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(new Date(currentDate?.getFullYear(), currentDate?.getMonth() - 1, 1));
    }
  };

  const goToNext = () => {
    if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(new Date(currentDate?.getFullYear(), currentDate?.getMonth() + 1, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Handle appointment request
  const handleRequestAppointment = (timeSlot) => {
    setSelectedTimeSlot(timeSlot);
    setIsRequestModalOpen(true);
  };

  const handleRequestSubmit = (requestData) => {
    console.log('Appointment request:', requestData);
    setIsRequestModalOpen(false);
    setSelectedTimeSlot(null);
    // Show success message
  };

  // Format date range
  const formatDateRange = () => {
    if (viewMode === 'week') {
      const startWeek = startOfWeek(currentDate, { locale: ptBR });
      const endWeek = addDays(startWeek, 6);
      return `${format(startWeek, 'dd MMM', { locale: ptBR })} - ${format(endWeek, 'dd MMM yyyy', { locale: ptBR })}`;
    } else {
      return format(currentDate, 'MMMM yyyy', { locale: ptBR });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{`Calendário de ${currentUser?.name} - Academy Platform`}</title>
        <meta name="description" content={`Visualizar disponibilidade e solicitar atendimento com ${currentUser?.name}`} />
      </Helmet>

      <Header />

      <main className="pt-16">
        <div className="container mx-auto px-6 py-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <nav className="text-sm text-muted-foreground">
              <span>Calendário</span>
              <span className="mx-2">/</span>
              <span className="text-foreground">{currentUser?.name}</span>
            </nav>
          </div>

          {/* User Info Panel */}
          <div className="mb-8">
            <UserInfoPanel user={currentUser} />
          </div>

          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground mb-2 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden">
                    <img 
                      src={currentUser?.avatar} 
                      alt={currentUser?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  Calendário de {currentUser?.name}
                </h1>
                <p className="text-muted-foreground">
                  <span className="inline-flex items-center gap-1 text-green-600">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Visualização somente leitura
                  </span> • Clique nos horários disponíveis para solicitar atendimento
                </p>
              </div>

              <div className="flex items-center gap-4">
                <Button
                  onClick={() => handleRequestAppointment({ date: new Date(), time: '10:00' })}
                  iconName="Calendar"
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
                >
                  Solicitar Atendimento
                </Button>
              </div>
            </div>
          </div>

          {/* Controls Section */}
          <div className="mb-8">
            <div className="bg-card rounded-lg border border-border p-6 shadow-tier-1">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                {/* Filter Controls */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Select
                    value={selectedUser}
                    onChange={setSelectedUser}
                    options={userOptions}
                    placeholder="Selecionar usuário"
                    className="w-full sm:w-64"
                  />
                  <Select
                    value={viewMode}
                    onChange={setViewMode}
                    options={viewModeOptions}
                    placeholder="Modo de visualização"
                    className="w-full sm:w-48"
                  />
                </div>

                {/* Navigation Controls */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={goToPrevious}
                    iconName="ChevronLeft"
                    size="sm"
                  />
                  <Button
                    variant="outline"
                    onClick={goToToday}
                    size="sm"
                    className="px-4"
                  >
                    Hoje
                  </Button>
                  <Button
                    variant="outline"
                    onClick={goToNext}
                    iconName="ChevronRight"
                    size="sm"
                  />
                  <div className="ml-4 text-sm font-medium text-foreground min-w-[200px] text-center">
                    {formatDateRange()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Section */}
          <div className="mb-8">
            <div className="bg-card rounded-lg border border-border shadow-tier-1 overflow-hidden">
              <div className="calendar-container">
                <UserCalendarGrid
                  currentDate={currentDate}
                  viewMode={viewMode}
                  events={userEvents}
                  onRequestAppointment={handleRequestAppointment}
                />
              </div>
            </div>
          </div>

          {/* Legend Section */}
          <div className="mb-8">
            <div className="bg-card rounded-lg border border-border p-6 shadow-tier-1">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Legenda
              </h3>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-success"></div>
                  <span className="text-sm text-muted-foreground">Disponível para agendamento</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-muted"></div>
                  <span className="text-sm text-muted-foreground">Ocupado (sem detalhes)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Appointment Request Modal */}
      {isRequestModalOpen && (
        <AppointmentRequestModal
          user={currentUser}
          timeSlot={selectedTimeSlot}
          isOpen={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
          onSubmit={handleRequestSubmit}
        />
      )}

      {/* Floating AI Chat */}
      <FloatingAIChat />
    </div>
  );
};

export default UserCalendarView;